import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'

const Icon = ({ id }: { id: string }) => (
  <svg className="ico">
    <use href={`#${id}`} />
  </svg>
)

export default function Header() {
  const [open, setOpen] = useState(false)
  const loc = useLocation()

  useEffect(() => {
    document.documentElement.classList.toggle('nav-open', open)
  }, [open])

  // close the drawer on navigation
  useEffect(() => {
    setOpen(false)
  }, [loc.pathname])

  return (
    <>
      <div className="topbar">
        <div className="wrap-wide">
          <div className="tb-left">
            Helping U.S. small businesses access the $700B+ federal marketplace
          </div>
          <div className="tb-right">
            <span>
              <span className="dotline">●</span> Mon–Fri · 9:00–6:00 ET
            </span>
            <span>(704) 459-8642</span>
          </div>
        </div>
      </div>

      <header className="site">
        <div className="wrap-wide">
          <Link className="brand" to="/">
            <span className="seal-logo">
              <img src="/atz-logo-white.png" alt="ATZ Solution" />
            </span>
            <span className="wm">
              <b>ATZ Solution LLC</b>
            </span>
          </Link>
          <nav className="main">
            <Link to="/">Home</Link>
            <div className="navitem has-mega">
              <Link to="/services/" className="megatrigger">
                Services{' '}
                <svg
                  className="caret"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </Link>
              <div className="mega">
                <div className="mega-inner">
                  <aside className="mega-feature">
                    <span className="seal">
                      <span>A</span>
                    </span>
                    <h4>Not sure where to begin?</h4>
                    <p>
                      Tell us about your business and a specialist will map the exact
                      registrations and certifications you qualify for — at no cost.
                    </p>
                    <Link className="btn btn-light" to="/contact-us/">
                      Talk to a Specialist <span className="arw">→</span>
                    </Link>
                    <div className="mega-feature-meta">
                      <span className="stars">
                        <Icon id="i-star" />
                        <Icon id="i-star" />
                        <Icon id="i-star" />
                        <Icon id="i-star" />
                        <Icon id="i-star" />
                      </span>
                      <span>4.9/5 · 2,400+ businesses served</span>
                    </div>
                  </aside>
                  <div className="mega-col">
                    <div className="mega-eyebrow">Services</div>
                    <Link className="mega-link" to="/sam-registration/">
                      <span className="mli">
                        <Icon id="i-doc" />
                      </span>
                      <span>
                        <b>SAM.gov Registration</b>
                        <span>Registration &amp; renewals</span>
                      </span>
                    </Link>
                    <Link className="mega-link" to="/services/#certifications">
                      <span className="mli">
                        <Icon id="i-award" />
                      </span>
                      <span>
                        <b>SBA Certification</b>
                        <span>Small Business Administration</span>
                      </span>
                    </Link>
                    <Link className="mega-link" to="/services/#services">
                      <span className="mli">
                        <Icon id="i-clipboard" />
                      </span>
                      <span>
                        <b>Capability Statement</b>
                        <span>Your federal company resume</span>
                      </span>
                    </Link>
                    <Link className="mega-link" to="/services/#services">
                      <span className="mli">
                        <Icon id="i-send" />
                      </span>
                      <span>
                        <b>Bid &amp; Proposal Support</b>
                        <span>Submission &amp; proposal writing</span>
                      </span>
                    </Link>
                    <Link className="mega-link" to="/services/#services">
                      <span className="mli">
                        <Icon id="i-target" />
                      </span>
                      <span>
                        <b>Consulting &amp; Strategy</b>
                        <span>NAICS &amp; set-aside guidance</span>
                      </span>
                    </Link>
                  </div>
                  <div className="mega-col mega-certs">
                    <div className="mega-eyebrow">Certifications</div>
                    <div className="cert-tiles">
                      <Link className="cert-tile" to="/services/#certifications">
                        <span className="ct-badge">
                          <Icon id="i-star" />
                        </span>
                        <b>WOSB / EDWOSB</b>
                        <span>Women-Owned</span>
                      </Link>
                      <Link className="cert-tile" to="/services/#certifications">
                        <span className="ct-badge">
                          <Icon id="i-shield" />
                        </span>
                        <b>VOSB / SDVOSB</b>
                        <span>Veteran-Owned</span>
                      </Link>
                      <Link className="cert-tile" to="/services/#certifications">
                        <span className="ct-badge">
                          <Icon id="i-mappin" />
                        </span>
                        <b>HUBZone</b>
                        <span>Underutilized zone</span>
                      </Link>
                      <Link className="cert-tile" to="/services/#certifications">
                        <span className="ct-badge">
                          <Icon id="i-award" />
                        </span>
                        <b>8(a) Program</b>
                        <span>Business Development</span>
                      </Link>
                    </div>
                    <Link className="mega-allcerts" to="/services/#certifications">
                      See all set-aside programs <span className="arw">→</span>
                    </Link>
                  </div>
                </div>
                <div
                  className="flagstripe header-stripe"
                  style={{ position: 'static', height: '3px' }}
                >
                  {Array.from({ length: 13 }).map((_, i) => (
                    <i key={i} />
                  ))}
                </div>
              </div>
            </div>
            <Link to="/portfolio/">Portfolio</Link>
            <Link to="/blogs/">Blogs</Link>
            <Link to="/contact-us/">Contact</Link>
            <Link to="/about-us/">About</Link>
          </nav>
          <div className="header-cta">
            <span className="phone">(704) 459-8642</span>
            <Link className="btn btn-primary sm" to="/contact-us/">
              Start Your Registration
            </Link>
            <button
              className="navtoggle"
              aria-label={open ? 'Close menu' : 'Open menu'}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>
        <div className="flagstripe header-stripe">
          {Array.from({ length: 13 }).map((_, i) => (
            <i key={i} />
          ))}
        </div>
      </header>

      <div className="mobilenav">
        <Link to="/">Home</Link>
        <Link to="/services/">Services</Link>
        <Link to="/services/#certifications">Certifications</Link>
        <Link to="/portfolio/">Portfolio</Link>
        <Link to="/blogs/">Blog</Link>
        <Link to="/about-us/">About</Link>
        <Link to="/contact-us/">Contact</Link>
        <Link className="btn btn-primary" to="/contact-us/">
          Start Your Registration
        </Link>
        <a className="mn-phone" href="tel:+17044598642">
          Call (704) 459-8642
        </a>
      </div>
      <div className="mobilenav-scrim" onClick={() => setOpen(false)} />
    </>
  )
}
